import { router } from '@teetsh/app/src/router';
import React from 'react';
import * as Fathom from 'fathom-client';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';

import 'typeface-inter';

import { initAcquisitionCookies } from 'cookies';
import { RouterProvider } from 'react-router-dom';
import { messageSW, Workbox } from 'workbox-window';
import GlobalStyle from 'GlobalStyles';
import posthog from 'posthog-js';
import { PostHogProviderWrapper } from 'PosthogProviderWrapper';
import './i18n/config';
import { CheckBrowserSupport } from 'CheckBrowserSupport';
import { focusManager } from '@tanstack/react-query';

if (import.meta.env.VITE_DISABLE_POSTHOG !== 'true') {
  posthog.init(import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_KEY, {
    api_host: 'https://d1cvwmq9rcje3n.cloudfront.net',
    ui_host: 'https://eu.posthog.com',
    autocapture: false,
    disable_session_recording: false, // let it false and handle activation through posthog UI
    capture_pageview: false,
    person_profiles: 'identified_only', // this is the *new* default, but we set it explicitly
    session_recording: {
      maskAllInputs: false,
    },
  });

  // Expose posthog to window for testing purposes (only in non-production environments)
  if (
    import.meta.env.VITE_ENVIRONMENT !== 'production' &&
    import.meta.env.VITE_DISABLE_POSTHOG !== 'true'
  ) {
    (window as any).posthog = posthog;
  }
}

// Expose React Query's focusManager for Playwright tests
if (import.meta.env.VITE_ENVIRONMENT !== 'production') {
  (window as any).focusManager = focusManager;
}

window.global ||= window;
initAcquisitionCookies();

if (process.env.NODE_ENV === 'production') {
  Fathom.load(import.meta.env.VITE_FATHOM_SITE_ID);
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENVIRONMENT,
    allowUrls: [/https?:\/\/((app|www)\.)?teetsh\.com/],
    ignoreErrors: [
      // Random plugins/extensions
      'top.GLOBALS',
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'http://loading.retry.widdit.com/',
      'atomicFindClose',
      // Facebook borked
      'fb_xd_fragment',
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
      // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      'conduitPage',
      // Generic error code from errors outside the security sandbox
      // You can delete this if using raven.js > 1.0, which ignores these automatically.
      'Script error.',
      'timeout of 0ms exceeded',
      "Unable to get property 'focus' of undefined or null reference",
      "Cannot read property 'focus' of undefined",
      'Network Error',
      'The operation is insecure.',
      'Unable to find node on an unmounted component.',
      "Cannot read property 'x' of null",
      "Unable to get property 'x' of undefined or null reference",
      'Failed to fetch',
      'SecurityError: SecurityError',
      'Non-Error promise rejection captured with keys: [object has no keys]',
      'Request failed with status code 401',
      "undefined is not an object (evaluating 'e.input.focus')",
      'n.quill.find is not a function',
      "undefined is not an object (evaluating 'o.nextSibling')",
      'tried to reject a fulfilled promise',
      'L’opération n’a pas pu s’achever. Erreur de protocole',
      'Script load failed',
      'Script URL https://app.teetsh.com/sw.js fetch resulted in error',
      'Script https://app.teetsh.com/sw.js load failed',
      'Failed to update a ServiceWorker: An unknown error occurred when fetching the script.',
      "tawk : Cannot read property 'name' of null",
      'e.composedPath is not a function',
      'diff() called with non-document',
      'Argument non valide',
      'Cannot update a service worker with a requested script URL whose newest worker has a different script URL',
      'InvalidStateError: newestWorker is null',
      "undefined is not an object (evaluating 't.input.focus')",
      // https://github.com/quilljs/quill/issues/2371
      '[Parchment] Cannot insert block into list-item',
      'NotSupportedError: Failed to get a ServiceWorkerRegistration: The user denied permission to use Service Worker.',
      /^Non-Error promise rejection captured with keys/,
      /^AbortError/,
      /^websocket error$/,
      /^Request failed with status code 404$/,
      /^InvalidStateError: An attempt was made/,
      /^Cannot read property 'error' of null/,
      /^Request failed with status code 400/,
      /^Request failed with status code 409/,
      /^ServiceWorker script at/,
      /^Cannot read property 'x' of null/,
      /^Failed to update a ServiceWorker/,
      /^Failed to update the ServiceWorker/,
      /^Failed to update ServiceWorker/,
      /^Unable to find node on an/,
      /^Socket server did not execute the callback for setAttributes with data/,
      /^Cannot read property 'visitorHandler'/,
      /^Loading chunk/,
      /^TypeError: Cannot read property 'focus' of null/,
      /^ResizeObserver/,
    ],
    beforeSend: event => {
      const { extra } = event;

      if (extra) {
        const { route, statusCode } = extra;
        if (
          route &&
          (route as string).endsWith('/v0/user') &&
          statusCode === 401
        ) {
          return null;
        }
      }

      return event;
    },
  });
}

// https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
const vh = window.innerHeight * 0.01;
document.documentElement.style.setProperty('--vh', `${vh}px`);

const container = document.getElementById('root');
const root = createRoot(container as any);
// Create the route tree using your routes
root.render(
  <PostHogProviderWrapper client={posthog}>
    <CheckBrowserSupport />
    <RouterProvider router={router} />
    <GlobalStyle />
  </PostHogProviderWrapper>,
);

// Install ServiceWorker and AppCache in the end since
// it's not most important operation and if main code fails,
// we do not want it installed
if (process.env.NODE_ENV === 'production') {
  if ('serviceWorker' in navigator) {
    const wb = new Workbox('/sw.js');
    let registration;
    wb.addEventListener('installed', () => {
      // eslint-disable-next-line no-console
      console.log('installed');
      (window as any).swUpdate = true;
    });
    wb.addEventListener('waiting', () => {
      (window as any).swUpdate = true;
      if (registration && registration.waiting) {
        // Send a message to the waiting service worker,
        // instructing it to activate.
        // Note: for this to work, you have to add a message
        // listener in your service worker. See below.
        messageSW(registration.waiting, { type: 'SKIP_WAITING' });
      }
    });
    wb.addEventListener('activated', event => {
      (window as any).swUpdate = true;
      // `event.isUpdate` will be true if another version of the service
      // worker was controlling the page when this version was registered.
      if (!event.isUpdate) {
        // eslint-disable-next-line no-console
        console.log('Service worker activated for the first time!');

        // If your service worker is configured to precache assets, those
        // assets should all be available now.
      }
    });
    // eslint-disable-next-line no-return-assign
    wb.register()
      .then(r => {
        registration = r;
        setInterval(() => {
          // eslint-disable-next-line no-console
          console.log('Checking for updates');
          return r
            ? r.update().catch(e =>
                Sentry.captureException(e, {
                  extra: {
                    details: 'Worker update failed',
                  },
                }),
              )
            : null;
          //   5min
        }, 300000);
      })
      .catch(e =>
        Sentry.captureException(e, {
          extra: {
            details: 'Worker register failed',
          },
        }),
      );
  }
}
